import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Menu, Users, ClipboardList, ScrollText, BarChart3, ListChecks, KeyRound, CalendarClock, FileSpreadsheet, MapPin, Activity, History, AlarmClock, PencilLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { LanguageToggle } from "@/components/LanguageToggle";
import { FaceEngineBanner } from "@/components/FaceEngineBanner";
import { BrandWordmark } from "@/components/Brand";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const NAV = {
  supervisor: [
    { to: "/staff", key: "clockInOut", icon: AlarmClock },
    { to: "/review", key: "reviewQueue", icon: ListChecks },
    { to: "/admin/health", key: "systemHealth", icon: Activity },
    { to: "/admin/reports", key: "reports", icon: FileSpreadsheet },
    { to: "/admin/shifts", key: "shifts", icon: CalendarClock },
    { to: "/admin/locations", key: "locations", icon: MapPin },
    { to: "/admin/users", key: "users", icon: Users },
    { to: "/admin/manual-punches", key: "manualPunches", icon: PencilLine },
  ],
  admin: [
    { to: "/staff", key: "clockInOut", icon: AlarmClock },
    { to: "/review", key: "reviewQueue", icon: ListChecks },
    { to: "/admin/punches", key: "allPunches", icon: ClipboardList },
    { to: "/admin/health", key: "systemHealth", icon: Activity },
    { to: "/admin/reports", key: "reports", icon: FileSpreadsheet },
    { to: "/admin/shifts", key: "shifts", icon: CalendarClock },
    { to: "/admin/locations", key: "locations", icon: MapPin },
    { to: "/admin/users", key: "users", icon: Users },
    { to: "/admin/overrides", key: "overridesNav", icon: History },
    { to: "/admin/manual-punches", key: "manualPunches", icon: PencilLine },
    { to: "/admin/audit", key: "auditLog", icon: ScrollText },
    { to: "/admin/activity", key: "supervisorActivity", icon: BarChart3 },
  ],
};

const NavLinks = ({ items, t, onNavigate }) => (
  <nav className="flex flex-col gap-1">
    {items.map(({ to, key, icon: Icon }) => (
      <NavLink
        key={to}
        to={to}
        onClick={onNavigate}
        data-testid={`nav-link-${key}`}
        className={({ isActive }) => cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
          isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary",
        )}
      >
        <Icon className="h-4 w-4" /> {t(key)}
      </NavLink>
    ))}
  </nav>
);

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const items = NAV[user?.role] || [];
  const isStaff = user?.role === "staff";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur-sm rk-safe-top">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex items-center gap-2">
            {!isStaff && items.length > 0 && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-nav-trigger">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64">
                  <SheetHeader>
                    <SheetTitle className="text-left">{t("appName")}</SheetTitle>
                  </SheetHeader>
                  <div className="mt-4">
                    <NavLinks items={items} t={t} />
                  </div>
                </SheetContent>
              </Sheet>
            )}
            <span data-testid="app-title">
              <BrandWordmark className="text-base" markClassName="h-7 w-7" />
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/change-password")}
              data-testid="change-password-nav-button"
              title={t("changePassword")}
            >
              <KeyRound className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button" title={t("logout")}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      <FaceEngineBanner />
      <div className="mx-auto flex max-w-6xl gap-6 px-4 py-6 sm:px-6">
        {!isStaff && items.length > 0 && (
          <aside className="hidden w-52 shrink-0 md:block">
            <NavLinks items={items} t={t} />
          </aside>
        )}
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
};
